/**
 * Accurate per-model, per-setting cost estimation in USD.
 *
 * Shared between server (billing) and client (cost display).
 * All costs sourced from fal.ai llms.txt per-model docs (verified Mar 2026).
 */

export interface CostEstimateInput {
  modelId: string;
  mediaType: 'image' | 'video' | 'text';
  size?: string;          // Image: '1K', '2K', '4K', 'square_hd', 'landscape_16_9_hd', etc.
  duration?: string;      // Video: '3', '4', '5', '6', '8', '10', '12', '15'
  resolution?: string;    // Video: '480p', '720p', '1080p'
  audioEnabled?: boolean; // Video: whether audio generation is on
  modelSettings?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Image costs (USD per generation)
// ---------------------------------------------------------------------------

const IMAGE_COSTS: Record<string, number | Record<string, number>> = {
  'gemini-3-pro-image-preview': { default: 0.134, '1K': 0.134, '2K': 0.134, '4K': 0.240 },
  'gemini-2.5-flash-image':     0.039,
  'minimax-image-01':           0.01,
  'luma-photon':                0.019,
  'flux-1.1-pro-ultra':         0.06,
  'recraft-v3':                 0.04,
  'recraft-v3-edit':            0.04,
  'flux-2-flex':                { default: 0.05, 'landscape_16_9_hd': 0.10, 'square_hd': 0.115 },
  'flux-kontext-pro':           0.04,
  'flux-kontext-pro-multi':     0.04,
  'flux-kontext-max':           0.08,
  'flux-kontext-max-multi':     0.08,
  'ideogram-v3-turbo':          0.03,
  'ideogram-v3':                0.06,
  'ideogram-v3-quality':        0.09,
  'ideogram-v3-character':      0.20,
  'gpt-image-1.5-low':          { default: 0.009, '1024x1024': 0.009, '1536x1024': 0.013, '1024x1536': 0.013 },
  'gpt-image-1.5-medium':       { default: 0.034, '1024x1024': 0.034, '1536x1024': 0.050, '1024x1536': 0.051 },
  'gpt-image-1.5-high':           { default: 0.133, '1024x1024': 0.133, '1536x1024': 0.199, '1024x1536': 0.200 },
  'gpt-image-1.5-edit-low':     { default: 0.009, '1024x1024': 0.009, '1536x1024': 0.013, '1024x1536': 0.013 },
  'gpt-image-1.5-edit-medium':  { default: 0.034, '1024x1024': 0.034, '1536x1024': 0.050, '1024x1536': 0.051 },
  'gpt-image-1.5-edit-high':      { default: 0.133, '1024x1024': 0.133, '1536x1024': 0.199, '1024x1536': 0.200 },
  // GPT Image 2 (via fal.ai). Flat-per-tier pricing — never undercharge:
  // each rate is the max fal-observed price in the tier's pixel range plus a
  // ~10% buffer. Source: documents/gpt-image-2-integration-plan.md §3.5.
  // Audit: 2026-04-22.
  'gpt-image-2-low':            { default: 0.01, '1K': 0.01, '2K': 0.02, '4K': 0.03 },
  'gpt-image-2-medium':         { default: 0.07, '1K': 0.07, '2K': 0.08, '4K': 0.13 },
  'gpt-image-2-high':           { default: 0.25, '1K': 0.25, '2K': 0.28, '4K': 0.45 },
  'gpt-image-2-edit-low':       { default: 0.01, '1K': 0.01, '2K': 0.02, '4K': 0.03 },
  'gpt-image-2-edit-medium':    { default: 0.07, '1K': 0.07, '2K': 0.08, '4K': 0.13 },
  'gpt-image-2-edit-high':      { default: 0.25, '1K': 0.25, '2K': 0.28, '4K': 0.45 },
  'seedream-v4':                { default: 0.03, 'auto_2K': 0.06, 'auto_4K': 0.06 },
  'seedream-v4.5':              0.04,
  'seedream-v4.5-edit':         0.04,
  'seedream-v5-lite-edit':      0.035,
  // New Gemini models
  'gemini-3.1-flash-image-preview': { default: 0.067, '512px': 0.045, '1K': 0.067, '2K': 0.101, '4K': 0.151 },
  'gemini-2.0-flash':           0.039,
};

const IMAGE_SIZE_MEGAPIXELS: Record<string, number> = {
  square: 1,
  square_hd: 2.3,
  portrait_4_3: 1,
  portrait_16_9: 1,
  landscape_4_3: 1,
  landscape_16_9: 1,
  landscape_16_9_hd: 2,
  auto: 1,
  auto_2K: 2,
  auto_4K: 4,
};

const IMAGE_MP_RATES: Record<string, { defaultSize: string; ratePerMegapixel: number }> = {
  'flux-schnell': { defaultSize: 'landscape_4_3', ratePerMegapixel: 0.003 },
  'flux-dev': { defaultSize: 'landscape_4_3', ratePerMegapixel: 0.025 },
  'qwen-image-edit-plus': { defaultSize: 'square_hd', ratePerMegapixel: 0.03 },
};

const GPT_IMAGE_15_COSTS: Record<string, Record<string, number>> = {
  low: {
    '1024x1024': 0.009,
    '1536x1024': 0.013,
    '1024x1536': 0.013,
  },
  medium: {
    '1024x1024': 0.034,
    '1536x1024': 0.05,
    '1024x1536': 0.051,
  },
  high: {
    '1024x1024': 0.133,
    '1536x1024': 0.199,
    '1024x1536': 0.2,
  },
};

const DEFAULT_IMAGE_COST = 0.04;

// ---------------------------------------------------------------------------
// Video costs (USD per clip, keyed by model → configuration)
// Models with audio tiers use { audioOff: { duration: cost }, audioOn: { duration: cost } }
// Models with resolution tiers use { resolution: cost }
// Models with duration-only tiers use { duration: cost }
// ---------------------------------------------------------------------------

interface AudioTieredCost {
  audioOff: Record<string, number>;
  audioOn: Record<string, number>;
}

type VideoCostEntry = number | Record<string, number> | AudioTieredCost;

function isAudioTiered(v: VideoCostEntry): v is AudioTieredCost {
  return typeof v === 'object' && 'audioOff' in v;
}

const VIDEO_COSTS: Record<string, VideoCostEntry> = {
  // Veo 3.1 Standard (t2v, i2v, flf) — $0.20/s silent, $0.40/s audio
  'veo-3.1':     { audioOff: { '4': 0.80, '6': 1.20, '8': 1.60 }, audioOn: { '4': 1.60, '6': 2.40, '8': 3.20 } },
  'veo-3.1-i2v': { audioOff: { '4': 0.80, '6': 1.20, '8': 1.60 }, audioOn: { '4': 1.60, '6': 2.40, '8': 3.20 } },
  'veo-3.1-flf': { audioOff: { '4': 0.80, '6': 1.20, '8': 1.60 }, audioOn: { '4': 1.60, '6': 2.40, '8': 3.20 } },

  // Veo 3.1 Ref — STANDARD tier (endpoint is veo3.1/reference-to-video, not /fast)
  // $0.20/s silent, $0.40/s audio at 720p/1080p
  'veo-3.1-ref':      { audioOff: { '8': 1.60 }, audioOn: { '8': 3.20 } },

  // Veo 3.1 Fast — $0.10/s silent, $0.15/s audio at 720p/1080p
  'veo-3.1-fast':     { audioOff: { '4': 0.40, '6': 0.60, '8': 0.80 }, audioOn: { '4': 0.60, '6': 0.90, '8': 1.20 } },
  'veo-3.1-fast-i2v': { audioOff: { '4': 0.40, '6': 0.60, '8': 0.80 }, audioOn: { '4': 0.60, '6': 0.90, '8': 1.20 } },

  // Sora 2 — $0.10/s, audio always on
  'sora-2-t2v': { '4': 0.40, '8': 0.80, '12': 1.20 },
  'sora-2-i2v': { '4': 0.40, '8': 0.80, '12': 1.20 },

  // Sora 2 Pro — 720p $0.30/s, 1080p $0.50/s
  'sora-2-pro-t2v': { '4_720p': 1.20, '8_720p': 2.40, '12_720p': 3.60, '4_1080p': 2.00, '8_1080p': 4.00, '12_1080p': 6.00 },
  'sora-2-pro-i2v': { '4_720p': 1.20, '8_720p': 2.40, '12_720p': 3.60, '4_1080p': 2.00, '8_1080p': 4.00, '12_1080p': 6.00 },

  // Kling 2.5 Turbo — $0.07/s flat
  'kling-2.5-turbo-t2v': { '5': 0.35, '8': 0.56, '10': 0.70 },
  'kling-2.5-turbo-i2v': { '5': 0.35, '8': 0.56, '10': 0.70 },

  // Kling 2.1 Pro — $0.098/s (fal llms.txt: 5s base=$0.49, then $0.098/s)
  'kling-2.1-pro-i2v': { '5': 0.49, '10': 0.98 },

  // Kling 3.0 Standard — $0.168/s silent, ~$0.252/s audio (per registry comment)
  'kling-3.0-std-t2v': { audioOff: { '5': 0.84, '8': 1.344, '10': 1.68, '15': 2.52 }, audioOn: { '5': 1.26, '8': 2.016, '10': 2.52, '15': 3.78 } },
  'kling-3.0-std-i2v': { audioOff: { '5': 0.84, '8': 1.344, '10': 1.68, '15': 2.52 }, audioOn: { '5': 1.26, '8': 2.016, '10': 2.52, '15': 3.78 } },

  // Kling 3.0 Pro — $0.224/s silent (per registry)
  'kling-3.0-pro-t2v': { audioOff: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 }, audioOn: { '5': 1.68, '8': 2.688, '10': 3.36, '15': 5.04 } },
  'kling-3.0-pro-i2v': { audioOff: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 }, audioOn: { '5': 1.68, '8': 2.688, '10': 3.36, '15': 5.04 } },

  // Kling O3 Standard — $0.168/s silent, $0.224/s audio (NOT same as 3.0!)
  'kling-o3-std-t2v': { audioOff: { '5': 0.84, '8': 1.344, '10': 1.68, '15': 2.52 }, audioOn: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 } },
  'kling-o3-std-i2v': { audioOff: { '5': 0.84, '8': 1.344, '10': 1.68, '15': 2.52 }, audioOn: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 } },

  // Kling O3 Pro — $0.224/s silent, $0.28/s audio (NOT same as 3.0 Pro!)
  'kling-o3-pro-t2v': { audioOff: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 }, audioOn: { '5': 1.40, '8': 2.24, '10': 2.80, '15': 4.20 } },
  'kling-o3-pro-ref': { audioOff: { '5': 1.12, '8': 1.792, '10': 2.24, '15': 3.36 }, audioOn: { '5': 1.40, '8': 2.24, '10': 2.80, '15': 4.20 } },

  // LTX Video 2.0 — $0.0018/megapixel (121 frames × resolution)
  // 720p: ~112 MP → $0.20, 1080p: ~251 MP → $0.45
  'ltx-2-t2v': { '720p': 0.20, '1080p': 0.45 },
  'ltx-2-i2v': { '720p': 0.20, '1080p': 0.45 },

  // WAN 2.2 — $0.08/s at 720p, $0.04/s at 480p (5s fixed duration)
  'wan-2.2-i2v': { '720p': 0.40, '480p': 0.20 },

  // Hailuo-02 Standard — $0.045/s
  'hailuo-02-t2v': 0.27,
  'hailuo-02-i2v': 0.27,

  // Hailuo-02 Pro — $0.08/s
  'hailuo-02-pro-t2v': { '6': 0.48, '10': 0.80 },
  'hailuo-02-pro-i2v': { '6': 0.48, '10': 0.80 },

  // Seedance 1.5 — token-based: $1.2/1M silent, $2.4/1M audio
  // tokens = (1280×720×24×duration)/1024 = 21,600 tokens/s at 720p
  'seedance-1.5-t2v': {
    audioOff: { '4': 0.104, '5': 0.130, '8': 0.207, '10': 0.259, '12': 0.311 },
    audioOn:  { '4': 0.207, '5': 0.259, '8': 0.415, '10': 0.518, '12': 0.622 },
  },
  'seedance-1.5-i2v': {
    audioOff: { '4': 0.104, '5': 0.130, '8': 0.207, '10': 0.259, '12': 0.311 },
    audioOn:  { '4': 0.207, '5': 0.259, '8': 0.415, '10': 0.518, '12': 0.622 },
  },

  // Seedance 1.0 Pro I2V — token-based: $2.5/1M tokens
  // 720p: 21,600 tokens/s → $0.054/s, 1080p: 48,600 tokens/s → $0.122/s
  'seedance-1.0-i2v': { '5_720p': 0.27, '10_720p': 0.54, '5_1080p': 0.61, '10_1080p': 1.22 },

  // Seedance 1.0 Fast — token-based: $1.0/1M tokens (720p only)
  // 720p: 21,600 tokens/s → $0.022/s
  'seedance-1.0-fast-t2v': { '5': 0.108, '10': 0.216 },
  'seedance-1.0-fast-i2v': { '5': 0.108, '10': 0.216 },
};

const DEFAULT_VIDEO_COST_PER_SECOND = 0.15;

export const USD_PER_CREDIT = 0.003;

// ---------------------------------------------------------------------------
// Credit pricing (customer-facing)
// Explicitly sourced from the 4K credit pricing doc where available.
// For current registry models/settings not listed there, fall back to
// nearest-integer conversion from base USD cost.
// ---------------------------------------------------------------------------

const IMAGE_CREDITS: Record<string, number | Record<string, number>> = {
  'gemini-3-pro-image-preview': { default: 45, '1K': 45, '2K': 45, '4K': 80 },
  'gemini-3.1-flash-image-preview': { default: 22, '512px': 15, '1K': 22, '2K': 34, '4K': 50 },
  'gemini-2.5-flash-image': 13,
  'recraft-v3': 13,
  'recraft-v3-edit': 13,
  'ideogram-v3-turbo': 10,
  'ideogram-v3-quality': 30,
  'gpt-image-1.5-low':      { default: 3,  '1024x1024': 3,  '1536x1024': 4,  '1024x1536': 4 },
  'gpt-image-1.5-medium':   { default: 11, '1024x1024': 11, '1536x1024': 17, '1024x1536': 17 },
  'gpt-image-1.5-high':       { default: 44, '1024x1024': 44, '1536x1024': 66, '1024x1536': 67 },
  'gpt-image-1.5-edit-low': { default: 3,  '1024x1024': 3,  '1536x1024': 4,  '1024x1536': 4 },
  'gpt-image-1.5-edit-medium': { default: 11, '1024x1024': 11, '1536x1024': 17, '1024x1536': 17 },
  'gpt-image-1.5-edit-high':  { default: 44, '1024x1024': 44, '1536x1024': 66, '1024x1536': 67 },
  // GPT Image 2 credits: derived from USD/0.003 USD_PER_CREDIT, rounded up to
  // preserve the never-undercharge margin against fal's published pricing.
  // See documents/gpt-image-2-integration-plan.md §3.5.
  'gpt-image-2-low':         { default: 3,  '1K': 3,  '2K': 7,   '4K': 10 },
  'gpt-image-2-medium':      { default: 23, '1K': 23, '2K': 27,  '4K': 43 },
  'gpt-image-2-high':        { default: 83, '1K': 83, '2K': 93,  '4K': 150 },
  'gpt-image-2-edit-low':    { default: 3,  '1K': 3,  '2K': 7,   '4K': 10 },
  'gpt-image-2-edit-medium': { default: 23, '1K': 23, '2K': 27,  '4K': 43 },
  'gpt-image-2-edit-high':   { default: 83, '1K': 83, '2K': 93,  '4K': 150 },
  'flux-2-flex': { default: 17, 'landscape_16_9_hd': 33, 'square_hd': 38 },
  'flux-kontext-pro': 13,
  'flux-kontext-pro-multi': 13,
  'seedream-v4': { default: 10, 'auto_2K': 20, 'auto_4K': 20 },
  'seedream-v4.5': 13,
  'seedream-v4.5-edit': 13,
};

const VIDEO_CREDITS: Record<string, VideoCostEntry> = {
  'veo-3.1': { audioOff: { '4': 267, '6': 400, '8': 533 }, audioOn: { '4': 533, '6': 800, '8': 1067 } },
  'veo-3.1-ref': { audioOff: { '8': 533 }, audioOn: { '8': 1067 } },
  'veo-3.1-fast': { audioOff: { '4': 133, '6': 200, '8': 267 }, audioOn: { '4': 200, '6': 300, '8': 400 } },
  'sora-2-t2v': { '4': 133, '8': 267, '12': 400 },
  'sora-2-i2v': { '4': 133, '8': 267, '12': 400 },
  'kling-2.5-turbo-t2v': { '5': 117, '8': 187, '10': 233 },
  'kling-2.5-turbo-i2v': { '5': 117, '8': 187, '10': 233 },
  'kling-3.0-std-t2v': {
    audioOff: { '5': 280, '8': 448, '10': 560, '15': 840 },
    audioOn: { '5': 420, '8': 672, '10': 840, '15': 1260 },
  },
  'kling-3.0-std-i2v': {
    audioOff: { '5': 280, '8': 448, '10': 560, '15': 840 },
    audioOn: { '5': 420, '8': 672, '10': 840, '15': 1260 },
  },
  'ltx-2-t2v': { '720p': 67, '1080p': 150 },
  'ltx-2-i2v': { '720p': 67, '1080p': 150 },
  'wan-2.2-i2v': { '720p': 133, '480p': 67 },
  'hailuo-02-t2v': 90,
  'hailuo-02-i2v': 90,
  'hailuo-02-pro-t2v': { '6': 160, '10': 267 },
  'hailuo-02-pro-i2v': { '6': 160, '10': 267 },
  'seedance-1.5-t2v': {
    audioOff: { '4': 35, '5': 43, '8': 69, '10': 86, '12': 104 },
    audioOn: { '4': 69, '5': 86, '8': 138, '10': 173, '12': 207 },
  },
  'seedance-1.5-i2v': {
    audioOff: { '4': 35, '5': 43, '8': 69, '10': 86, '12': 104 },
    audioOn: { '4': 69, '5': 86, '8': 138, '10': 173, '12': 207 },
  },
  'seedance-1.0-i2v': { '5_720p': 90, '10_720p': 180, '5_1080p': 203, '10_1080p': 407 },
  'seedance-1.0-fast-t2v': { '5': 36, '10': 72 },
  'seedance-1.0-fast-i2v': { '5': 36, '10': 72 },
};

// ---------------------------------------------------------------------------
// Main cost estimator
// ---------------------------------------------------------------------------

function _getGptImage15CostUsd(size?: string, quality?: string): number {
  const effectiveQuality = quality && quality in GPT_IMAGE_15_COSTS ? quality : 'medium';
  const sizeCosts = GPT_IMAGE_15_COSTS[effectiveQuality];
  const normalizedSize = size === 'auto' ? '1024x1024' : size;
  const effectiveSize =
    normalizedSize && sizeCosts[normalizedSize] !== undefined ? normalizedSize : '1024x1024';
  return sizeCosts[effectiveSize];
}

function getImageCostUsd(
  modelId: string,
  size?: string,
  _modelSettings?: Record<string, unknown>,
): number {
  const entry = IMAGE_COSTS[modelId];
  if (entry === undefined) return DEFAULT_IMAGE_COST;
  if (typeof entry === 'number') return entry;
  // Object with size keys
  if (size && entry[size] !== undefined) return entry[size];
  return entry['default'] ?? DEFAULT_IMAGE_COST;
}

function getMegapixelImageCostUsd(modelId: string, size?: string): number | null {
  const rateDef = IMAGE_MP_RATES[modelId];
  if (!rateDef) return null;

  const effectiveSize =
    size && IMAGE_SIZE_MEGAPIXELS[size] !== undefined ? size : rateDef.defaultSize;
  const megapixels = IMAGE_SIZE_MEGAPIXELS[effectiveSize];
  if (megapixels === undefined) return null;
  return megapixels * rateDef.ratePerMegapixel;
}

function getVideoCostUsd(modelId: string, duration?: string, resolution?: string, audioEnabled?: boolean): number {
  const entry = VIDEO_COSTS[modelId];
  if (entry === undefined) {
    // Fallback: use default rate × requested duration
    const dur = parseFloat(duration || '5');
    return DEFAULT_VIDEO_COST_PER_SECOND * dur;
  }

  // Flat cost (single number, e.g. hailuo-02)
  if (typeof entry === 'number') return entry;

  // Audio-tiered models (veo, seedance, kling 3.0/O3)
  if (isAudioTiered(entry)) {
    const tier = audioEnabled !== false ? entry.audioOn : entry.audioOff;
    const dur = duration || '5';
    if (tier[dur] !== undefined) return tier[dur];
    // Interpolate: find closest duration or use rate
    const availableDurations = Object.keys(tier).map(Number).sort((a, b) => a - b);
    const requestedDur = parseFloat(dur);
    // Use the closest available duration
    let closest = availableDurations[0];
    for (const d of availableDurations) {
      if (Math.abs(d - requestedDur) < Math.abs(closest - requestedDur)) closest = d;
    }
    return tier[String(closest)] ?? DEFAULT_VIDEO_COST_PER_SECOND * requestedDur;
  }

  // Duration-only keyed (sora, kling 2.5 turbo, etc.)
  const obj = entry as Record<string, number>;

  // Resolution-based models (LTX, WAN) — keys are resolution strings like '720p', '1080p'
  if (obj['720p'] !== undefined) {
    const res = resolution || '720p';
    return obj[res] ?? obj['720p'];
  }

  // Composite keys like '4_720p', '8_1080p' (Sora 2 Pro, Seedance 1.0 Pro)
  const compositeKey = `${duration || '5'}_${resolution || '720p'}`;
  if (obj[compositeKey] !== undefined) return obj[compositeKey];

  // Simple duration keys
  const dur = duration || '5';
  if (obj[dur] !== undefined) return obj[dur];

  // Fallback: closest duration
  const availableDurations = Object.keys(obj).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b);
  if (availableDurations.length > 0) {
    const requestedDur = parseFloat(dur);
    let closest = availableDurations[0];
    for (const d of availableDurations) {
      if (Math.abs(d - requestedDur) < Math.abs(closest - requestedDur)) closest = d;
    }
    return obj[String(closest)] ?? DEFAULT_VIDEO_COST_PER_SECOND * requestedDur;
  }

  return DEFAULT_VIDEO_COST_PER_SECOND * parseFloat(dur);
}

function getImageCredits(modelId: string, size?: string): number | null {
  const entry = IMAGE_CREDITS[modelId];
  if (entry === undefined) return null;
  if (typeof entry === 'number') return entry;
  if (size && entry[size] !== undefined) return entry[size];
  return entry['default'] ?? null;
}

function getVideoCredits(
  modelId: string,
  duration?: string,
  resolution?: string,
  audioEnabled?: boolean,
): number | null {
  const entry = VIDEO_CREDITS[modelId];
  if (entry === undefined) return null;
  if (typeof entry === 'number') return entry;

  if (isAudioTiered(entry)) {
    const tier = audioEnabled !== false ? entry.audioOn : entry.audioOff;
    const dur = duration || '5';
    if (tier[dur] !== undefined) return tier[dur];
    return null;
  }

  const obj = entry as Record<string, number>;
  if (obj['720p'] !== undefined) {
    const res = resolution || '720p';
    return obj[res] ?? obj['720p'] ?? null;
  }

  const compositeKey = `${duration || '5'}_${resolution || '720p'}`;
  if (obj[compositeKey] !== undefined) return obj[compositeKey];

  const dur = duration || '5';
  return obj[dur] ?? null;
}

export function convertUsdToCredits(costUsd: number): number {
  if (costUsd <= 0) return 0;
  return Math.max(1, Math.round(costUsd / USD_PER_CREDIT));
}

/**
 * Returns the estimated base API cost in USD for a generation request.
 * This is the cost BEFORE any markup is applied.
 *
 * Used for:
 * - Pre-flight checks: creditService.assertSufficientCredits(orgId, credits)
 * - Post-generation debit fallback conversion when no explicit credit price exists
 * - UI cost display in generation settings panel
 */
export function getBaseCostUsd(input: CostEstimateInput): number {
  switch (input.mediaType) {
    case 'text':
      return 0; // Text is free; gate it with creditService.assertHasActivePlan()
    case 'image': {
      const megapixelCost = getMegapixelImageCostUsd(input.modelId, input.size);
      return megapixelCost ?? getImageCostUsd(input.modelId, input.size, input.modelSettings);
    }
    case 'video':
      return getVideoCostUsd(input.modelId, input.duration, input.resolution, input.audioEnabled);
    default:
      return 0;
  }
}

/**
 * Returns the customer-facing credit estimate for a generation request.
 * Uses explicit doc pricing where available, then falls back to base USD cost.
 */
export function getEstimatedCredits(input: CostEstimateInput): number {
  switch (input.mediaType) {
    case 'text':
      return 0;
    case 'image': {
      const explicitCredits = getImageCredits(input.modelId, input.size);
      if (explicitCredits != null) return explicitCredits;
      return convertUsdToCredits(getBaseCostUsd(input));
    }
    case 'video': {
      const explicitCredits = getVideoCredits(
        input.modelId,
        input.duration,
        input.resolution,
        input.audioEnabled,
      );
      if (explicitCredits != null) return explicitCredits;
      return convertUsdToCredits(getBaseCostUsd(input));
    }
    default:
      return 0;
  }
}
